package com.essntl.features.organization.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import com.essntl.features.file.ui.essntlgallery.EssntlGallery
import com.essntl.features.file.ui.essntlgallery.EssntlGalleryState
import com.essntl.uikit.components.button.outline.EsMediumOutlineButton
import com.essntl.uikit.components.button.tinted.EsMediumTintedButton
import com.essntl.uikit.components.dropdown.EsInputDropdownMenu
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.icons.EsMediumIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.input.label.EsInputLabel
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlinx.coroutines.delay
import com.essntl.core.unsplash.ui.UnsplashImage
import com.essntl.features.address.domain.model.AddressModel
import com.essntl.features.address.ui.AddressEvent
import com.essntl.features.address.ui.AddressScreen
import com.essntl.features.address.ui.AddressState
import com.essntl.features.address.ui.AddressValidation
import com.essntl.features.organization.domain.model.OrganizationModel
import com.essntl.uikit.components.file.FileViewer

@Composable
fun OrganizationScreen(
    state: OrganizationState,
    onEvent: (OrganizationEvent) -> Unit,
    validation: OrganizationValidation,
    addressState: AddressState,
    onAddressEvent: (AddressEvent) -> Unit,
    addressValidation: AddressValidation,
    creation: Boolean = true,
    asParentOrg: Boolean = false,
) {
    var organizationSearchText by rememberSaveable { mutableStateOf("") }

    var newOrganization by rememberSaveable { mutableStateOf(false) }

    var openEssntlGalleryDialog by rememberSaveable { mutableStateOf(false) }

    var fileUrl by rememberSaveable { mutableStateOf("") }
    var isFileViewerOpened by rememberSaveable { mutableStateOf(false) }

    LaunchedEffect(
        organizationSearchText,
        state.organization.value.type,
    ) {
        if (state.organization.value.type != null) {
            delay(500)
            onEvent(OrganizationEvent.SearchOrganization(organizationSearchText))
        }
    }

    LaunchedEffect(state.addAddress) {
        if (!state.addAddress) {
            onAddressEvent(AddressEvent.UpdateAddressFields(AddressModel.empty))
            addressValidation.addressForm.reset()
        }
    }

    Column(
        verticalArrangement = Arrangement.Center,
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = MaterialTheme.esColorScheme.bgSecondary,
                shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
            )
            .clip(
                RoundedCornerShape(
                    topStart = 24.dp,
                    topEnd = 24.dp,
                ),
            )
            .padding(
                horizontal = if (asParentOrg) 1.spacing else 2.5f.spacing,
                vertical = if (asParentOrg) 0.spacing else 2.spacing,
            ),
    ) {
        if (newOrganization) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(1.5.spacing),
                modifier = Modifier
                    .clip(RoundedCornerShape(2.spacing))
                    .clickable {
                        newOrganization = false
                        onEvent(
                            OrganizationEvent.UpdateOrganizationFields(
                                OrganizationModel.empty.copy(
                                    type = state.organization.value.type,
                                ),
                            ),
                        )
                        onEvent(OrganizationEvent.AddRemoveAddress(false))
                        validation.organizationForm.reset()
                    }
                    .padding(1.spacing),
            ) {
                EsMediumIcon(res = EsIcons.ArrowLeft)

                Text(
                    text = "Back to selection",
                    textAlign = TextAlign.Start,
                    style = MaterialTheme.esTypography.p2Regular14,
                )
            }

            Spacer(modifier = Modifier.height(2.spacing))
        }

        if (!asParentOrg) {
            Text(
                text = "Organization information",
                textAlign = TextAlign.Start,
                style = MaterialTheme.esTypography.p1Bold16,
            )

            Spacer(modifier = Modifier.height(1.spacing))
        }

        if (!newOrganization)
            EsInputDropdownMenu(
                items = state.getAllOrganizations.data.filter { it.id != state.organization.value.id },
                selectedItem = if (asParentOrg)
                    state.getAllOrganizations.data.find { it.id == state.selectedParentOrganization.value.id }
                else
                    state.organization.value,
                text = { it.name },
                onItemSelected = {
                    validation.organizationForm.name.validate(it.name)
                    if (asParentOrg) {
                        onEvent(OrganizationEvent.SetParentOrganization(it))
                    } else {
                        onEvent(OrganizationEvent.UpdateOrganizationFields(it))
                    }
                },
                label = if (asParentOrg) "Parent Organization" else "Name",
                placeholder = "Select or add new",
                message = "This field is required",
                state = validation.organizationForm.name.state,
                loading = state.getAllOrganizations.isLoading &&
                    state.getAllOrganizations.data.isEmpty(),
                topContent = { fm ->
                    Spacer(Modifier.height(0.5.spacing))
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(1.spacing),
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        EsInput(
                            value = organizationSearchText,
                            onValueChange = {
                                organizationSearchText = it
                            },
                            placeholder = "Search",
                            prefix = { EsMediumIcon(res = EsIcons.Search) },
                            modifier = Modifier.weight(3f),
                        )

                        if (creation)
                            Row(
                                horizontalArrangement = Arrangement.spacedBy(1.5.spacing),
                                modifier = Modifier
                                    .clip(RoundedCornerShape(2.spacing))
                                    .clickable {
                                        fm.clearFocus()
                                        onEvent(
                                            OrganizationEvent.UpdateOrganizationFields(
                                                OrganizationModel.empty.copy(
                                                    type = state.organization.value.type,
                                                ),
                                            ),
                                        )
                                        newOrganization = true
                                        validation.organizationForm.reset()
                                    }
                                    .padding(1.spacing)
                                    .weight(1f),
                            ) {
                                Text(
                                    text = "Create new",
                                    textAlign = TextAlign.Start,
                                    style = MaterialTheme.esTypography.p2Regular14,
                                    color = MaterialTheme.esColorScheme.textAccent,
                                )

                                EsMediumIcon(
                                    res = EsIcons.Add,
                                    tint = MaterialTheme.esColorScheme.iconAccent,
                                )
                            }
                    }
                    Spacer(Modifier.height(1.spacing))
                },
                emptyContent = {
                    Text(
                        text = "No organization found!",
                        style = MaterialTheme.esTypography.p2Regular14,
                    )
                },
            ) {
                if (!state.getAllOrganizations.isLastPage) {
                    Spacer(modifier = Modifier.height(2.spacing))

                    EsMediumOutlineButton(
                        onClick = {
                            onEvent(OrganizationEvent.LoadOrganizations())
                        },
                        text = "Load more",
                        loading = state.getAllOrganizations.isLoading,
                        modifier = Modifier.align(Alignment.CenterHorizontally),
                    )
                }
            }
        else {
            Row(
                horizontalArrangement = Arrangement.spacedBy(3.spacing),
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth(),
            ) {
                EsInput(
                    value = if (asParentOrg) "the selected parent" else state.organization.value.name,
                    onValueChange = {
                        validation.organizationForm.name.validate(it)

                        onEvent(
                            OrganizationEvent.UpdateOrganizationFields(
                                state.organization.value.copy(name = it),
                            ),
                        )
                    },
                    label = "Name",
                    placeholder = "Enter name",
                    message = "This field is required",
                    state = validation.organizationForm.name.state,
                    modifier = Modifier.weight(2f),
                )

                Column(
                    verticalArrangement = Arrangement.Center,
                    modifier = Modifier
                        .weight(1f)
                        .padding(bottom = 2.spacing),
                ) {
                    EsInputLabel(
                        label = "Logo",
                        modifier = Modifier,
                    )

                    Spacer(
                        modifier = Modifier.height(.5f.spacing),
                    )

                    state.fileState.getAllFile.data?.firstOrNull()?.let { file ->
                        UnsplashImage(
                            url = file.url,
                            selectable = false,
                            imageSize = DpSize(264.dp, 160.dp),
                            onDelete = { onEvent(OrganizationEvent.DeleteLogo) },
                            onClick = {
                                isFileViewerOpened = true
                                fileUrl = file.url
                            },
                        )
                    } ?: EsMediumTintedButton(
                        text = "Add a logo",
                        onClick = { openEssntlGalleryDialog = true },
                    )
                }
            }

            Spacer(modifier = Modifier.height(1.spacing))

            EsInput(
                value = state.organization.value.website.orEmpty(),
                onValueChange = {
                    onEvent(
                        OrganizationEvent.UpdateOrganizationFields(
                            state.organization.value.copy(website = it),
                        ),
                    )
                },
                label = "Website",
                placeholder = "Add website",
            )

            Spacer(modifier = Modifier.height(1.spacing))

            EsInput(
                value = state.organization.value.notes.orEmpty(),
                onValueChange = {
                    onEvent(
                        OrganizationEvent.UpdateOrganizationFields(
                            state.organization.value.copy(notes = it),
                        ),
                    )
                },
                singleLine = false,
                label = "Notes",
                placeholder = "Add notes",
            )

            Spacer(modifier = Modifier.height(2.5.spacing))

            OrganizationScreen(
                state = state,
                onEvent = onEvent,
                validation = validation,
                addressState = addressState,
                onAddressEvent = onAddressEvent,
                addressValidation = addressValidation,
                creation = false,
                asParentOrg = true,
            )

            Spacer(modifier = Modifier.height(2.5.spacing))

            Row(
                horizontalArrangement = Arrangement.spacedBy(1.5.spacing),
                modifier = Modifier
                    .clip(RoundedCornerShape(2.spacing))
                    .clickable { onEvent(OrganizationEvent.AddRemoveAddress()) }
                    .padding(1.spacing),
            ) {
                Text(
                    text = if (state.addAddress)
                        "Remove address" else
                        "Add address",
                    textAlign = TextAlign.Start,
                    style = MaterialTheme.esTypography.p1Medium16,
                    color = if (state.addAddress)
                        MaterialTheme.esColorScheme.textError else
                        MaterialTheme.esColorScheme.textAccent,
                )

                EsLargeIcon(
                    res = if (state.addAddress)
                        EsIcons.ArrowDown else
                        EsIcons.Add,
                    tint = if (state.addAddress)
                        MaterialTheme.esColorScheme.iconError else
                        MaterialTheme.esColorScheme.iconAccent,
                )
            }

            if (state.addAddress)
                AddressScreen(
                    state = addressState,
                    onEvent = onAddressEvent,
                    validation = addressValidation,
                    innerScreen = true,
                )
        }
    }

    if (openEssntlGalleryDialog)
        EssntlGallery(
            entity = if (state.organization.value.type == OrganizationModel.Type.TravelAgency)
                EssntlGalleryState.Entity.TA_LOGOS else
                EssntlGalleryState.Entity.SUPPLIER_LOGOS,
            isOpen = openEssntlGalleryDialog,
            onDismissRequest = { openEssntlGalleryDialog = false },
            multipleSelect = false,
            onAddImages = {
                val image = it.firstOrNull() ?: return@EssntlGallery
                onEvent(OrganizationEvent.AddLogo(image))
            },
        )

    FileViewer(
        fileUrl = fileUrl,
        isDialogOpened = isFileViewerOpened,
        onDismissRequest = {
            isFileViewerOpened = false
            fileUrl = ""
        },
    )
}